import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef, Injectable, NgZone, PLATFORM_ID, computed, effect, inject, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import { ResizeFim, ResizeMovimento } from '../directives/one-resize.directive';
import { TourRoteiroService } from './tour-mode/tour-roteiro.service';
import { AiAgentService } from './API/ai-agent.service';

export type ModoAssistente = 'palco' | 'chat' | 'painel';

/** Piso do arrasto da coluna acoplada (E8). */
export const AI_PAINEL_LARGURA_MIN_PX = 250;
/** Largura padrão/teto da coluna acoplada (spec §4.2) — a mesma do cartão flutuante. */
export const AI_PAINEL_LARGURA_PX = 400;
/** Quanto além do piso o ponteiro precisa passar para o arrasto virar intenção de fechar (gaveta). */
export const AI_PAINEL_FECHA_ALEM_PX = 40;
/** Piso de viewport para acoplar (spec D9): 400 de painel + 54 de menu deixam 826 para listas. */
export const AI_PAINEL_PISO_VIEWPORT_PX = 1280;
/** Variável CSS lida pelo `.main-wrapper` (padding-right) e pelos `fixed` à direita. */
export const AI_PAINEL_CSS_VAR = '--ai-painel-largura';

/** Persistida entre sessões (E8). O MODO em si deixou de ser persistido — ver `definirModo`. */
const STORAGE_LARGURA = 'ai_painel_largura';
/** M3 (review final): chaves legadas de versões anteriores ao E8, quando o modo ERA persistido.
 *  Nunca eram limpas — ficavam mortas no localStorage do usuário para sempre. */
const STORAGE_MODO_LEGADO = 'ai_modo';
const STORAGE_CHAT_MODE_LEGADO = 'ai_chat_mode';
const RESIZE_DEBOUNCE_MS = 150;
/** Casa com a duração da transição CSS de `.ai-painel` (ai-painel.component.css). */
const RESIZE_SINCRONICO_MS = 300;

/**
 * Dono do MODO do assistente e do empurrão do layout (spec §4.3).
 *
 * D8 — "tour ativo ⇒ nunca há empurrão": sai do `computed` `modoEfetivo`, não de código
 * imperativo. Tour começa → efetivo cai para `chat` (cartão flutuante); tour termina → volta a
 * `painel` sozinho, se essa era a escolha salva. O mesmo vale para viewport abaixo do piso.
 */
@Injectable({ providedIn: 'root' })
export class AiLayoutService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly doc = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tourRoteiro = inject(TourRoteiroService);
  private readonly ai = inject(AiAgentService);

  private readonly ehNavegador = isPlatformBrowser(this.platformId);

  private readonly _modo = signal<ModoAssistente>(this.lerModoSalvo());
  readonly modo = this._modo.asReadonly();

  private readonly _larguraViewport = signal<number>(this.lerLargura());
  readonly larguraViewport = this._larguraViewport.asReadonly();

  /** Largura persistida da coluna acoplada (E8), clamp 250–400, default 400. */
  private readonly _largura = signal<number>(this.lerLarguraSalva());
  readonly largura = this._largura.asReadonly();
  /** Largura + intenção de fechar do arrasto em curso; `null` fora de arrasto. */
  private readonly _arrastando = signal<ResizeMovimento | null>(null);
  readonly arrastando = this._arrastando.asReadonly();
  /** Largura que o CSS deve mostrar agora: a do arrasto em curso ou a persistida. */
  readonly larguraEfetiva = computed(() => this._arrastando()?.largura ?? this._largura());

  /** Handle do resize sintético (I1) — cancelado a cada novo run do effect para que
   *  alternâncias rápidas de dock/undock disparem só o último. */
  private resizeSincronicoTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Review Task 7 (Importante 1): o empurrão (`.main-wrapper` etc.) só existe dentro do shell ERP
   * (`showMenu` do AppComponent) — PDV e qualquer rota `data.menu === false` não têm
   * `.main-wrapper` nenhum para empurrar. Sem esta guarda, o painel acoplado flutuava por cima de
   * telas sem o shell, cobrindo conteúdo em vez de deslocá-lo. `AppComponent` escreve aqui a cada
   * `NavigationEnd`, logo após decidir `showMenu`.
   */
  readonly shellComMenu = signal(false);

  readonly podeAcoplar = computed(() =>
    !this.tourRoteiro.ativo()
    && this._larguraViewport() >= AI_PAINEL_PISO_VIEWPORT_PX
    && this.shellComMenu());

  readonly modoEfetivo = computed<ModoAssistente>(() => {
    const m = this._modo();
    if (m !== 'painel') return m;
    return this.podeAcoplar() ? 'painel' : 'chat';
  });

  readonly acoplado = computed(() => this.ai.aberto() && this.modoEfetivo() === 'painel');

  constructor() {
    if (!this.ehNavegador) return;
    // E8: modo deixou de ser persistido; limpa as chaves das versões anteriores.
    try {
      localStorage.removeItem(STORAGE_MODO_LEGADO);
      localStorage.removeItem(STORAGE_CHAT_MODE_LEGADO);
    } catch {
      // storage bloqueado (modo privado/política): nada a limpar nesta sessão
    }
    const win = this.doc.defaultView;
    if (win) {
      // Fora da zona + debounce: o mesmo remédio do resize da advanced-table (travava o PC).
      // Re-entra na zona para disparar change detection em componentes observadores.
      this.zone.runOutsideAngular(() => {
        fromEvent(win, 'resize')
          .pipe(debounceTime(RESIZE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.zone.run(() => this._larguraViewport.set(win.innerWidth)));
      });
    }
    // O resize sintético abaixo não pode sobreviver ao serviço: um timer órfão dispararia
    // 'resize' num window que já tem outro dono (em teste, o serviço seguinte — flake real).
    this.destroyRef.onDestroy(() => {
      if (this.resizeSincronicoTimer) clearTimeout(this.resizeSincronicoTimer);
      this.resizeSincronicoTimer = null;
    });
    effect(() => {
      const largura = this.acoplado() ? `${this.larguraEfetiva()}px` : '0px';
      this.doc.documentElement.style.setProperty(AI_PAINEL_CSS_VAR, largura);
      // I1 (review final do painel acoplado): dock/undock muda a largura útil do conteúdo
      // (padding-right do .main-wrapper, via esta mesma variável CSS) sem emitir um 'resize'
      // nativo — a advanced-table (e qualquer observador de layout baseado nesse evento) nunca
      // remedia colunas. Sincroniza um resize sintético depois da transição CSS do painel
      // (0.28s, ai-painel.component.css). Idempotente para o listener debounced acima (mesma
      // largura ⇒ sem mudança de estado). Cancela o timer anterior: alternâncias rápidas
      // disparam só o último.
      if (win) {
        if (this.resizeSincronicoTimer) clearTimeout(this.resizeSincronicoTimer);
        this.resizeSincronicoTimer = setTimeout(() => {
          this.resizeSincronicoTimer = null;
          win.dispatchEvent(new Event('resize'));
        }, RESIZE_SINCRONICO_MS);
      }
    });
  }

  /** E8: modo deixou de ser persistido — cada sessão nasce no default de `lerModoSalvo`. */
  definirModo(m: ModoAssistente): void {
    this._modo.set(m);
  }

  definirLargura(px: number): void {
    const v = this.clampLargura(px);
    this._largura.set(v);
    if (!this.ehNavegador) return;
    try {
      localStorage.setItem(STORAGE_LARGURA, String(v));
    } catch {
      // storage bloqueado (modo privado/política): a escolha vale só nesta sessão
    }
  }

  iniciarArrasto(): void {
    this._arrastando.set({ largura: this._largura(), vaiFechar: false });
  }

  moverArrasto(m: ResizeMovimento): void {
    this._arrastando.set({ largura: this.clampLargura(m.largura), vaiFechar: m.vaiFechar });
  }

  /** Fim do arrasto: `fechar` fecha o assistente e NÃO persiste a largura do arrasto (fica a
   *  anterior); caso contrário persiste a largura final via `definirLargura`. */
  terminarArrasto(f: ResizeFim): void {
    this._arrastando.set(null);
    if (f.fechar) {
      this.ai.fechar();
      return;
    }
    this.definirLargura(f.largura);
  }

  /** Cancela o arrasto (Escape) sem persistir nada — volta à largura anterior. */
  cancelarArrasto(): void {
    this._arrastando.set(null);
  }

  private clampLargura(v: number): number {
    if (!Number.isFinite(v)) return AI_PAINEL_LARGURA_PX;
    return Math.min(AI_PAINEL_LARGURA_PX, Math.max(AI_PAINEL_LARGURA_MIN_PX, Math.round(v)));
  }

  /** E8: modo inicial é `painel` no browser (nada mais é lido do storage) e `chat` no servidor. */
  private lerModoSalvo(): ModoAssistente {
    return this.ehNavegador ? 'painel' : 'chat';
  }

  private lerLargura(): number {
    if (!this.ehNavegador) return 0;
    return this.doc.defaultView?.innerWidth ?? 0;
  }

  /** `getItem() === null` (nada salvo) tem que virar o default 400 — `Number(null)` é 0, que
   *  clamparia para 250 e mentiria sobre "nada salvo". Não-numérico (`Number('abc')` = NaN) já
   *  cai no default via `clampLargura`'s `Number.isFinite`. */
  private lerLarguraSalva(): number {
    if (!this.ehNavegador) return AI_PAINEL_LARGURA_PX;
    try {
      const salvo = localStorage.getItem(STORAGE_LARGURA);
      if (salvo === null) return AI_PAINEL_LARGURA_PX;
      return this.clampLargura(Number(salvo));
    } catch {
      return AI_PAINEL_LARGURA_PX;
    }
  }
}
